import React from 'react';
import { User, Briefcase, GraduationCap, FileText } from 'lucide-react';
import { sectionTitles, defaultPlaceholderData } from '../common/resumeConstants';
import { 
  checkHasRealData, 
  getCurrentData, 
  getPaperTypeText
} from '../common/resumeHelpers';
import { 
  ContactInfo,
  ResumeSection,
  SummarySection,
  CoreCompetenciesSection,
  ExperienceSection,
  EducationSection,
  CertificateSection,
  PaperSection,
  SkillSection,
  getPlaceholderClass
} from '../common/ResumeComponents';

// Template 20 - 초 깔끔한 사이드바 디자인
const Template20 = ({ resumeData, updatingField, language = 'kr' }) => {
  const titles = sectionTitles[language] || sectionTitles.kr;
  const hasRealData = checkHasRealData(resumeData);
  const currentData = getCurrentData(resumeData, defaultPlaceholderData, hasRealData);

  return (
    <div className={`w-full max-w-4xl mx-auto bg-white ${language === 'en' ? 'resume-english' : ''}`} style={{ fontFamily: "'Pretendard Variable', 'Pretendard', -apple-system, BlinkMacSystemFont, sans-serif" }}>
      <link rel="stylesheet" as="style" crossorigin href="https://cdn.jsdelivr.net/gh/orioncactus/pretendard@v1.3.9/dist/web/variable/pretendardvariable-dynamic-subset.min.css" />
      
      <div className="flex min-h-[1123px]">
        {/* 왼쪽 사이드바 - 30% */}
        <aside className="w-[30%] bg-gray-50 p-8">
          {/* 이름과 직함 */}
          <div className="mb-10">
            <h1 className={getPlaceholderClass(hasRealData.name, "text-2xl font-bold text-gray-900 mb-2", "text-2xl font-bold text-gray-400 mb-2 italic")}>
              {currentData.name}
            </h1>
            <p className={getPlaceholderClass(hasRealData.title, "text-sm text-gray-600 uppercase tracking-wider", "text-sm text-gray-400 uppercase tracking-wider italic")}>
              {currentData.title}
            </p>
          </div>

          {/* 연락처 */}
          <div className="mb-8">
            <h3 className="text-xs font-bold text-gray-900 uppercase tracking-wider mb-4">{titles.contact}</h3>
            <div className="space-y-2 text-xs">
              {currentData.phone && (
                <div className={getPlaceholderClass(hasRealData.phone, "text-gray-600", "text-gray-400 italic")}>
                  {currentData.phone}
                </div>
              )}
              {currentData.email && (
                <div className={getPlaceholderClass(hasRealData.email, "text-gray-600", "text-gray-400 italic")}>
                  {currentData.email}
                </div>
              )}
              {currentData.address && (
                <div className={getPlaceholderClass(hasRealData.address, "text-gray-600", "text-gray-400 italic")}>
                  {currentData.address}
                </div>
              )}
              {currentData.socialLinks && currentData.socialLinks.map((social, index) => (
                <div key={index} className="text-gray-600">
                  {social.url.replace(/^https?:\/\/(www\.)?/, '')}
                </div>
              ))}
            </div>
          </div>

          {/* 학력 */}
          <div className="mb-8">
            <h3 className="text-xs font-bold text-gray-900 uppercase tracking-wider mb-4">{titles.education}</h3>
            <EducationSection
              education={currentData.education}
              hasRealData={hasRealData}
              datePosition="top"
              degreeClassName="text-sm font-medium text-gray-800"
              schoolClassName="text-xs text-gray-600"
              majorClassName="text-xs text-gray-600"
              dateClassName="text-xs text-gray-500 mb-1"
              showMajor={true}
              itemClassName="mb-4 last:mb-0"
            />
          </div>

          {/* 기술 */}
          <div className="mb-8">
            <h3 className="text-xs font-bold text-gray-900 uppercase tracking-wider mb-4">{titles.skills}</h3>
            <SkillSection
              skills={currentData.skills}
              hasRealData={hasRealData}
              variant="inline"
              className="text-xs text-gray-600"
              separator=", "
            />
          </div>

          {/* 언어 */}
          {currentData.languages && currentData.languages.length > 0 && (
            <div className="mb-8">
              <h3 className="text-xs font-bold text-gray-900 uppercase tracking-wider mb-4">{titles.languages}</h3>
              <div className="space-y-2">
                {currentData.languages.map((lang, index) => (
                  <div key={index} className="flex justify-between text-xs">
                    <span className="text-gray-700">{lang.language}</span>
                    <span className="text-gray-500">{lang.level}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* 자격증 */}
          {currentData.certificates.length > 0 && (
            <div>
              <h3 className="text-xs font-bold text-gray-900 uppercase tracking-wider mb-4">{titles.certificates}</h3>
              <CertificateSection
                certificates={currentData.certificates}
                hasRealData={hasRealData}
                className="space-y-3"
                nameClassName="text-xs font-medium text-gray-700"
                issuerClassName="text-xs text-gray-500"
                dateClassName="text-xs text-gray-500"
                dateFormat="combined"
              />
            </div>
          )}
        </aside>

        {/* 오른쪽 메인 콘텐츠 - 70% */}
        <main className="flex-1 p-10">
          {/* 소개 */}
          {currentData.summary && (
            <section className="mb-10">
              <h2 className="text-sm font-bold text-gray-900 uppercase tracking-wider mb-4">{titles.introduction}</h2>
              <SummarySection
                summary={currentData.summary}
                hasRealData={hasRealData}
                textClassName="text-sm text-gray-700 leading-relaxed"
              />
            </section>
          )}

          {/* 핵심 역량 */}
          {currentData.keyStrengths.length > 0 && (
            <section className="mb-10">
              <h2 className="text-sm font-bold text-gray-900 uppercase tracking-wider mb-4">{titles.coreCompetencies}</h2>
              <div className="space-y-2">
                {currentData.keyStrengths.map((strength, index) => {
                  const colonIndex = strength.indexOf(':');
                  const hasColon = colonIndex !== -1;
                  const title = hasColon ? strength.substring(0, colonIndex) : strength;
                  const description = hasColon ? strength.substring(colonIndex) : '';
                  
                  return (
                    <div key={index} className={getPlaceholderClass(hasRealData.keyStrengths, "text-sm text-gray-700", "text-sm text-gray-400 italic")}>
                      {hasColon ? (
                        <>
                          <span className="font-semibold">{title}</span>{description}
                        </>
                      ) : (
                        strength
                      )}
                    </div>
                  );
                })}
              </div>
            </section>
          )}

          {/* 경력 사항 */}
          <section className="mb-10">
            <h2 className="text-sm font-bold text-gray-900 uppercase tracking-wider mb-6">{titles.experience}</h2>
            <ExperienceSection
              experiences={currentData.experiences}
              hasRealData={hasRealData}
              datePosition="right"
              companyClassName="text-base font-semibold text-gray-900"
              positionClassName="text-sm text-gray-600 mb-2"
              dateClassName="text-xs text-gray-500"
              descriptionClassName="text-sm text-gray-700 leading-relaxed mb-3"
              projectTitleClassName="text-sm font-medium text-gray-800 mb-1"
              projectDescriptionClassName="text-sm text-gray-600"
              projectIndent="pl-0"
              itemClassName="mb-8 last:mb-0 pb-8 last:pb-0 border-b last:border-0 border-gray-100"
            />
          </section>

          {/* 논문 */}
          {hasRealData.papers && (
            <section>
              <h2 className="text-sm font-bold text-gray-900 uppercase tracking-wider mb-6">{titles.papers}</h2>
              <PaperSection
                papers={currentData.papers}
                hasRealData={hasRealData}
                getPaperTypeText={getPaperTypeText}
                titleClassName="text-sm font-medium text-gray-800"
                authorsClassName="text-xs text-gray-600"
                journalClassName="text-xs text-gray-600"
                typeClassName="ml-2 px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded"
              />
            </section>
          )}
        </main>
      </div>
    </div>
  );
};

export default Template20;